import config from 'app/config';
import template from './opform.tpl.html';
import 'app/common/styles/_opform.scss';

// Caches declared outside the class, accessible to all instances.
const formFetchCache = new Map();
const scriptLoadCache = new Map();

// Constants for clarity and easy maintenance
const DEFAULT_POLLING_RETRIES = 10;
const DEFAULT_POLLING_DELAY_MS = 200;

class OntraportFormController {

  // Ensure all dependencies are correctly listed for minification safety
  static $inject = [
    "$window", "$ocLazyLoad", "$sce", "$timeout", "userService", "PageService", "$element", "$q"
  ];

  /** @ngInject */
  constructor($window, $ocLazyLoad, $sce, $timeout, userService, PageService, $element, $q) {

    Object.assign(this, {
      $window, $ocLazyLoad, $sce, $timeout, userService, PageService, $element, $q
    });
    this.config = config;

    // Component State
    this.page = null;
    this.opformConfig = {};
    this.trustedHtmlForm = '';
    this.isLoading = false;
    this.hasError = false;
  }

  $onInit() {
    this.initializeForm();
  }

  /**
   * Main entry point for form initialization. Orchestrates the steps.
   */
  async initializeForm() {
    // Prevent re-initialization if already loading or loaded
    if (this.isLoading || this.trustedHtmlForm) {
      return;
    }

    let formId = null;
    let ontraportScriptPath = null;

    try {
      this._resetFormState(); // Sets isLoading = true

      await this._getAndSetPage(); // Step 1: Fetch and set page data
      formId = this._getFormId(); // Step 2: Build config and get form ID

      if (!formId) {
        throw new Error('Ontraport form ID is not defined in opformConfig.');
      }

      // Step 3: Fetch form data with caching
      const formDataResponse = await this._fetchFormData(formId);
      const rawHtmlString = formDataResponse.data; // Extract raw HTML string

      // Step 4: Load Ontraport script with caching
      ontraportScriptPath = `https://forms.ontraport.com/v2.4/include/formEditor/genjs-v3.php?html=false&uid=${formId}`;
      await this._loadOntraportScript(ontraportScriptPath);

      // Step 5: Sanitize and trust the HTML for rendering
      this.trustedHtmlForm = this._sanitizeAndTrustHtml(rawHtmlString);

      // Step 6: Robust pre-fill - Pass queryParams once to the prefill method
      const queryParams = this.userService.getQueryParamObject();
      this._prefillFormWhenReady(queryParams);

    } catch (error) {
      this._handleInitializationError(error, formId, ontraportScriptPath);
    } finally {
      this.isLoading = false; // Ensure loading state is reset
    }
  }

  /**
   * Resets the loading and error state of the component.
   */
  _resetFormState() {
    this.isLoading = true;
    this.hasError = false;
  }

  /**
   * Fetches the global page object and sets it on the controller instance.
   */
  async _getAndSetPage() {
    let currentPage = this.PageService.getGlobalPage();
    if (currentPage instanceof Promise) {
      currentPage = await currentPage;
    }
    this.page = currentPage;
  }

  /**
   * Builds the form configuration based on component bindings and page data,
   * then returns the derived form ID.
   * @returns {string|null} The Ontraport form ID.
   */
  _getFormId() {
    // Ensure this.page and its properties are safely accessed
    const pageFormConfig = this.page?.options?.hsform || {};

    this.opformConfig = {
      formid: this.formid || pageFormConfig.id,
      btntext: this.btntext || this.page?.options?.button?.text,
      properties: this.properties || pageFormConfig.properties,
      btnclass: this.btnclass || 'btn btn-lg btn-warning mt-0',
      email: this.userService.getQueryParam('email', '') || this.userService.getQueryParam('Email', '') // Keep if other parts use this directly
    };

    return this.opformConfig.formid;
  }

  /**
   * Fetches the Ontraport form data from the API, utilizing a cache.
   * @param {string} formId - The ID of the Ontraport form.
   * @returns {Promise<Object>} The raw API response object.
   * @throws {Error} If the form data cannot be loaded.
   */
  async _fetchFormData(formId) {
    let apiResponse;

    if (formFetchCache.has(formId)) {
      apiResponse = await formFetchCache.get(formId);
    } else {
      const fetchDataPromise = this.$q.resolve().then(async () => {
        const basepath = config.appName === 'mrs' ? 'page' : config.appName;
        const apiUrl = `${basepath}/op/${config.appName}/form/${formId}`;

        const rawApiResult = await this.PageService.getRowV3(apiUrl);

        // Destructure to get the actual status and data (HTML string) from the API response
        const { status, data: htmlContent } = rawApiResult;

        if (status !== 'ok' || !htmlContent) {
          throw new Error(`Failed to load form data for FormId ${formId}. Server response: ` + JSON.stringify(rawApiResult));
        }

        // Ensure htmlContent is a string
        if (typeof htmlContent !== 'string') {
          rawApiResult.data = String(htmlContent || '');
        }

        return rawApiResult;
      });

      formFetchCache.set(formId, fetchDataPromise);
      apiResponse = await fetchDataPromise;
    }
    return apiResponse;
  }

  /**
   * Loads the Ontraport external script, utilizing a cache.
   * @param {string} scriptPath - The URL of the Ontraport script.
   * @returns {Promise<void>} A promise that resolves when the script is loaded.
   */
  async _loadOntraportScript(scriptPath) {
    if (scriptLoadCache.has(scriptPath)) {
      await scriptLoadCache.get(scriptPath);
    } else {
      const loadScriptPromise = this.$ocLazyLoad.load({ type: 'js', path: scriptPath });
      scriptLoadCache.set(scriptPath, loadScriptPromise);
      await loadScriptPromise;
    }
  }

  /**
   * Handles errors during form initialization, logs them, sets error state,
   * and cleans up caches.
   * @param {Error} error - The error object.
   * @param {string} formId - The form ID involved (for cache cleanup).
   * @param {string} ontraportScriptPath - The script path involved (for cache cleanup).
   */
  _handleInitializationError(error, formId, ontraportScriptPath) {
    console.error("[OntraportFormController] Form initialization error:", error);
    this.hasError = true;
    this.isLoading = false; // Ensure loading is reset on error
    if (formId && formFetchCache.has(formId)) {
      formFetchCache.delete(formId);
    }
    if (ontraportScriptPath && scriptLoadCache.has(ontraportScriptPath)) {
      scriptLoadCache.delete(ontraportScriptPath);
    }
  }

  /**
   * Sanitize HTML content for safe rendering and trust it for Angular binding.
   * This method now attempts to parse the HTML and remove `<script>` and `<link>` tags
   * to mitigate potential XSS risks and ensure a cleaner DOM.
   * @param {string} htmlString - The raw HTML string received from the API.
   * @returns {*} The trusted HTML object for Angular's $sce.
   */
  _sanitizeAndTrustHtml(htmlString) {
    // Create a temporary div element to parse the HTML string
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;

    // Remove script and link tags for security and cleaner DOM
    tempDiv.querySelectorAll('script, link').forEach(tag => tag.remove());

    // Return the innerHTML of the sanitized div, trusted by $sce
    return this.$sce.trustAsHtml(tempDiv.innerHTML);
  }

  /**
   * Attempts to pre-fill form fields with user data from query parameters.
   * Uses a recursive $timeout to poll for the form element's readiness,
   * specifically targeting the form within this component's scope using its unique UID.
   *
   * @param {Object} queryParams - Pre-parsed object of query parameters.
   * @param {number} retries - Number of remaining attempts to find the form.
   * @param {number} delay - Delay in milliseconds between retries.
   */
  _prefillFormWhenReady(queryParams, retries = DEFAULT_POLLING_RETRIES, delay = DEFAULT_POLLING_DELAY_MS) {
    if (retries <= 0) {
      console.warn(`Ontraport Form (ID: ${this.opformConfig.formid}): Could not find the expected form structure for pre-fill after multiple attempts.`);
      return;
    }

    this.$timeout(() => {
      // Find the specific form element within THIS component's scope
      const formElement = this.$element[0].querySelector(`form input[type="hidden"][name="uid"][value="${this.opformConfig.formid}"]`)?.closest('form');

      if (formElement) {
        const fieldsToFill = [
          'firstname', 'fname', // Common aliases for first name
          'lastname', 'lname', // Common aliases for last name
          'email',
          'contact_id',
          'sms_number',
          'utm_content', 'utm_source', 'utm_medium', 'utm_term', 'utm_campaign',
          'referral_page',
          '_op_gclid', '_op_gcid', '_fbc', '_fbp', '_op_li_fat_id'
        ];

        fieldsToFill.forEach(prop => {
          // Check for the value in queryParams, handling aliases like 'email' vs 'Email'
          const value = queryParams[prop] || queryParams[prop.charAt(0).toUpperCase() + prop.slice(1)] || '';

          if (value) {
            // Find nodes with exact name or prefixed name within the specific form
            const nodes = formElement.querySelectorAll(
              `[name="${prop}"], [name="primary_contact//${prop}"]`
            );

            nodes.forEach(node => {
              // Only pre-fill if the field is currently empty or has a placeholder
              if (node.value === '' || node.value === node.placeholder) {
                node.value = value;
                // Dispatch 'change' and 'input' events to ensure Angular/Ontraport's internal
                // listeners pick up the programmatically set value, especially for validation.
                node.dispatchEvent(new Event('input', { bubbles: true }));
                node.dispatchEvent(new Event('change', { bubbles: true }));
              }
            });
          }
        });
      } else {
        // Form element not found yet, retry
        this._prefillFormWhenReady(queryParams, retries - 1, delay);
      }
    }, delay);
  }
}


// --- Component Definition ---
class OntraportFormComponent {
  constructor() {
    this.bindings = {
      formid: '@?',
      btntext: '@?',
      formtarget: '@?',
      btnclass: '@?',
      properties: '=?',
    };
    this.template = template;
    this.controller = OntraportFormController;
  }
}

export default angular
  .module('opform.component', [])
  .component('opform', new OntraportFormComponent())
  .name;